import {
  Button,
  Card,
  Form,
  Input,
  Table,
  Tag,
  Typography,
  message,
  Space,
  Row,
  Col,
  Select,
} from "antd";
import moment from "moment";
import React, { useEffect, useRef, useState } from "react";
import { connect } from "react-redux";
import { getCustomers } from "../../RestServices/authService";
import {
  createSubscription,
  deleteSubscription,
  getSubscriptionsByCustomer,
  updateSubscription,
  getAllSubscriptions,
  getSubscriptionSummary,
} from "../../RestServices/postingService";
import { formateString } from "../../shared";
import { MAIN_BACKGROUND_COLOR } from "../../styleConstants";
import SubscriptionModal from "./SubscriptionModal";
import "./customers.css";

const { Title } = Typography;

const Customers = ({ Auth }) => {
  const [customers, setCustomers] = useState([]);
  const [loading, setLoading] = useState(false);
  // searchInput = what the admin is typing (updates every keystroke).
  // searchText  = debounced value that actually triggers the fetch.
  const [searchInput, setSearchInput] = useState("");
  const [searchText, setSearchText] = useState("");
  const [pagination, setPagination] = useState({
    current: 1,
    pageSize: 20,
    total: 0,
  });
  const [subscriptionModalVisible, setSubscriptionModalVisible] = useState(false);
  const [selectedCustomer, setSelectedCustomer] = useState(null);
  const [subscriptions, setSubscriptions] = useState([]);
  const [subscriptionLoading, setSubscriptionLoading] = useState(false);
  const [subscriptionFilter, setSubscriptionFilter] = useState("all");
  const [paidSort, setPaidSort] = useState("none"); // none | desc | asc
  const [form] = Form.useForm();
  // Guards against stale responses overwriting newer ones when the admin
  // types/clicks fast — only the latest request may update state.
  const fetchIdRef = useRef(0);

  // Debounce: wait 500ms after the admin stops typing before searching.
  // Without this every keystroke fired a request to both backends.
  useEffect(() => {
    if (searchInput === searchText) return undefined;
    const timer = setTimeout(() => {
      setPagination((p) => ({ ...p, current: 1 }));
      setSearchText(searchInput);
    }, 500);
    return () => clearTimeout(timer);
  }, [searchInput, searchText]);

  useEffect(() => {
    fetchCustomers();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    pagination.current,
    pagination.pageSize,
    searchText,
    subscriptionFilter,
    paidSort,
  ]);

  const fetchCustomers = async () => {
    const fetchId = ++fetchIdRef.current;
    try {
      setLoading(true);
      let rows = [];
      let total = 0;

      // Paid-amount sorting is computed by the subscriptions backend, so
      // any paid sort (and the Active filter) uses the subscription listing.
      const useSubscriptionListing =
        subscriptionFilter === "active" || paidSort !== "none";

      if (useSubscriptionListing) {
        // ── Subscription-backed listing (server-side everything) ───────
        // One call: dedup per customer, joined customer profile, totalPaid,
        // server-side search + sort + pagination. No client-side joins.
        const params = {
          pageNo: pagination.current,
          limit: pagination.pageSize,
        };
        if (subscriptionFilter === "active") params.isActive = true;
        if (searchText && searchText.trim()) params.search = searchText.trim();
        if (paidSort !== "none") {
          params.sortBy = "totalPaid";
          params.sortOrder = paidSort;
        }

        const subData = await getAllSubscriptions(params, Auth.user.token);
        if (fetchId !== fetchIdRef.current) return; // stale response
        const subDocs = subData.subscriptions?.docs || [];
        total = subData.subscriptions?.totalDocs || 0;

        rows = subDocs.map((sub) => {
          const cust = sub.customer || {};
          return {
            key: sub.customerId,
            _id: sub.customerId,
            name: cust.name || "Unknown customer",
            phoneNumber: cust.phoneNumber || "N/A",
            email: cust.email,
            city: cust.city || "N/A",
            status: cust.status,
            profileImage: cust.profileImage,
            createdAt: cust.createdAt
              ? moment(cust.createdAt).format(formateString)
              : "N/A",
            type: cust.type || "N/A",
            subscription: sub,
            totalPaid: sub.totalPaid || 0,
            subscriptionCount: sub.subscriptionCount || 0,
          };
        });
      } else {
        // ── All Customers view ─────────────────────────────────────────
        const params = {
          page: pagination.current,
          limit: pagination.pageSize,
        };
        if (searchText && searchText.trim()) {
          params.search = searchText.trim();
        }

        const data = await getCustomers(Auth.user.token, params);
        if (fetchId !== fetchIdRef.current) return; // stale response
        const docs = data.customers?.docs || [];
        total = data.customers?.totalDocs || 0;

        // One bulk call for paid totals + live subscription status of the
        // customers on this page (replaces the old "fetch 100 active subs
        // and hope the right ones are there" approach).
        let summary = {};
        if (docs.length > 0) {
          try {
            const summaryData = await getSubscriptionSummary(
              docs.map((c) => c._id),
              Auth.user.token
            );
            if (fetchId !== fetchIdRef.current) return; // stale response
            summary = summaryData.summary || {};
          } catch (subErr) {
            // Summary lookup failed — don't block the customer list
            console.warn("Failed to fetch subscription summary", subErr);
          }
        }

        rows = docs.map((customer) => {
          const info = summary[customer._id] || {};
          return {
            key: customer._id,
            _id: customer._id,
            name: customer.name,
            phoneNumber: customer.phoneNumber,
            email: customer.email,
            city: customer.city,
            status: customer.status,
            profileImage: customer.profileImage,
            createdAt: moment(customer.createdAt).format(formateString),
            updatedAt: moment(
              customer.lastProfileFetched || customer.updatedAt
            ).format(formateString),
            type: customer.profileDetails?.type || "N/A",
            subscription: info.activeSubscription || null,
            totalPaid: info.totalPaid || 0,
            subscriptionCount: info.subscriptionCount || 0,
          };
        });
      }

      setCustomers(rows);
      setPagination((prev) => ({ ...prev, total }));
    } catch (err) {
      if (fetchId !== fetchIdRef.current) return;
      message.error(err.response?.data?.message || "Failed to fetch customers");
    } finally {
      if (fetchId === fetchIdRef.current) setLoading(false);
    }
  };

  const handleTableChange = (newPagination) => {
    setPagination({
      current: newPagination.current,
      pageSize: newPagination.pageSize,
      total: pagination.total,
    });
  };

  const handleSearchChange = (e) => {
    // Only updates the input — the debounce effect fires the search.
    setSearchInput(e.target.value);
  };

  const handleSearch = (value) => {
    // Enter / Search button → skip the debounce, search immediately
    setPagination({
      ...pagination,
      current: 1,
    });
    setSearchInput(value || "");
    setSearchText(value || "");
    // fetchCustomers will be called automatically via useEffect
  };

  const handleClearSearch = () => {
    setSearchInput("");
    setSearchText("");
    setSubscriptionFilter("all");
    setPaidSort("none");
    // Reset to first page when clearing search
    setPagination({
      ...pagination,
      current: 1,
    });
    // fetchCustomers will be called automatically via useEffect
  };

  const handleViewSubscriptions = async (customer) => {
    try {
      setSubscriptionLoading(true);
      setSelectedCustomer(customer);
      const response = await getSubscriptionsByCustomer(customer._id, Auth.user.token);
      setSubscriptions(response.subscriptions || []);
      setSubscriptionModalVisible(true);
    } catch (err) {
      message.error(
        err.response?.data?.message || "Failed to fetch subscriptions"
      );
    } finally {
      setSubscriptionLoading(false);
    }
  };

  const handleCreateSubscription = () => {
    setSelectedCustomer(null);
    form.resetFields();
    setSubscriptionModalVisible(true);
  };

  const handleSubscriptionSubmit = async (values) => {
    try {
      const customerId = selectedCustomer?._id || values.customerId;
      if (!customerId) {
        message.error("Please select a customer first");
        return;
      }
      const payload = {
        customerId,
        startDate: moment(values.startDate).toISOString(),
        endDate: moment(values.endDate).toISOString(),
        paidAmount: Number(values.paidAmount) || 0,
        notes: values.notes || "",
      };

      if (values.subscriptionId) {
        // Update existing subscription — include the admin-chosen status.
        // The backend syncs AI-intelligence access off this flag, so
        // setting Inactive revokes the customer's access immediately.
        if (typeof values.isActive === "boolean") {
          payload.isActive = values.isActive;
        }
        await updateSubscription(values.subscriptionId, payload, Auth.user.token);
        message.success("Subscription updated successfully");
      } else {
        // Create new subscription
        await createSubscription(payload, Auth.user.token);
        message.success("Subscription created successfully");
      }

      setSubscriptionModalVisible(false);
      form.resetFields();
      setSelectedCustomer(null);

      // Refresh subscriptions if viewing a customer's subscriptions
      if (selectedCustomer) {
        await handleViewSubscriptions(selectedCustomer);
      }
      // ALWAYS refresh the customer table — without this the new
      // subscription is invisible on the page (user clicks Add → modal
      // closes → table looks unchanged → "it didn't work").
      await fetchCustomers();
    } catch (err) {
      message.error(
        err.response?.data?.message ||
          err.response?.data?.detail ||
          "Failed to save subscription"
      );
    }
  };

  const handleDeleteSubscription = async (subscriptionId) => {
    try {
      await deleteSubscription(subscriptionId, Auth.user.token);
      message.success("Subscription deleted successfully");

      // Refresh subscriptions
      if (selectedCustomer) {
        await handleViewSubscriptions(selectedCustomer);
      }
    } catch (err) {
      message.error(
        err.response?.data?.message || "Failed to delete subscription"
      );
    }
  };

  const isSubscriptionActive = (subscription) => {
    if (!subscription.isActive) return false;
    const now = moment();
    const startDate = moment(subscription.startDate);
    const endDate = moment(subscription.endDate);
    return now.isBetween(startDate, endDate, null, "[]");
  };

  const columns = [
    {
      title: "Sr.",
      width: 60,
      render: (text, record, index) =>
        (pagination.current - 1) * pagination.pageSize + index + 1,
    },
    {
      title: "Name",
      dataIndex: "name",
      key: "name",
      render: (text, record) => (
        <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
          {record.profileImage && (
            <img
              src={record.profileImage}
              alt={text}
              style={{
                width: "32px",
                height: "32px",
                borderRadius: "50%",
                objectFit: "cover",
              }}
            />
          )}
          <span>{text || "N/A"}</span>
        </div>
      ),
      onHeaderCell: () => ({
        style: {
          backgroundColor: MAIN_BACKGROUND_COLOR,
          color: "white",
          fontWeight: "bold",
        },
      }),
    },
    {
      title: "Phone Number",
      dataIndex: "phoneNumber",
      key: "phoneNumber",
      onHeaderCell: () => ({
        style: {
          backgroundColor: MAIN_BACKGROUND_COLOR,
          color: "white",
          fontWeight: "bold",
        },
      }),
    },
    {
      title: "City",
      dataIndex: "city",
      key: "city",
      onHeaderCell: () => ({
        style: {
          backgroundColor: MAIN_BACKGROUND_COLOR,
          color: "white",
          fontWeight: "bold",
        },
      }),
    },
    {
      title: "Type",
      dataIndex: "type",
      key: "type",
      onHeaderCell: () => ({
        style: {
          backgroundColor: MAIN_BACKGROUND_COLOR,
          color: "white",
          fontWeight: "bold",
        },
      }),
    },
    {
      title: "Status",
      dataIndex: "status",
      key: "status",
      render: (status) => (
        <Tag color={status === "active" ? "green" : "default"}>{status}</Tag>
      ),
      onHeaderCell: () => ({
        style: {
          backgroundColor: MAIN_BACKGROUND_COLOR,
          color: "white",
          fontWeight: "bold",
        },
      }),
    },
    {
      title: "Subscription",
      key: "subscription",
      render: (text, record) => {
        if (record.subscription) {
          const ended = moment(record.subscription.endDate).isBefore(moment());
          return (
            <Tag color={ended ? "red" : "blue"}>
              {ended ? "Ended" : "Ends"}:{" "}
              {moment(record.subscription.endDate).format("DD-MM-YYYY")}
            </Tag>
          );
        }
        return <Tag>None</Tag>;
      },
      onHeaderCell: () => ({
        style: {
          backgroundColor: MAIN_BACKGROUND_COLOR,
          color: "white",
          fontWeight: "bold",
        },
      }),
    },
    {
      title: "Total Paid",
      dataIndex: "totalPaid",
      key: "totalPaid",
      render: (totalPaid, record) => (
        <span style={{ fontWeight: 500 }}>
          Rs {Number(totalPaid || 0).toLocaleString()}
          {record.subscriptionCount > 1 && (
            <span style={{ color: "#999", fontWeight: 400 }}>
              {" "}
              ({record.subscriptionCount} subs)
            </span>
          )}
        </span>
      ),
      onHeaderCell: () => ({
        style: {
          backgroundColor: MAIN_BACKGROUND_COLOR,
          color: "white",
          fontWeight: "bold",
        },
      }),
    },
    {
      title: "Created At",
      dataIndex: "createdAt",
      key: "createdAt",
      onHeaderCell: () => ({
        style: {
          backgroundColor: MAIN_BACKGROUND_COLOR,
          color: "white",
          fontWeight: "bold",
        },
      }),
    },
    {
      title: "Actions",
      key: "actions",
      render: (text, record) => (
        <Space>
          <Button
            type="primary"
            size="small"
            onClick={() => handleViewSubscriptions(record)}
          >
            View Subscriptions
          </Button>
        </Space>
      ),
      onHeaderCell: () => ({
        style: {
          backgroundColor: MAIN_BACKGROUND_COLOR,
          color: "white",
          fontWeight: "bold",
        },
      }),
    },
  ];

  return (
    <div className="customers-container">
      <Card>
        <div className="flex-between" style={{ marginBottom: "20px" }}>
          <Title level={3} style={{ marginBottom: 0 }}>
            Customers
          </Title>
          <Button type="primary" onClick={handleCreateSubscription}>
            Add Subscription
          </Button>
        </div>

        <Row gutter={[16, 16]} style={{ marginBottom: "16px" }}>
          <Col xs={24} sm={24} md={12} lg={8} xl={8}>
            <Input.Search
              placeholder="Search by name or phone number"
              allowClear
              enterButton="Search"
              size="large"
              value={searchInput}
              onChange={handleSearchChange}
              onSearch={handleSearch}
              style={{ width: "100%" }}
            />
          </Col>
          <Col xs={24} sm={24} md={12} lg={8} xl={6}>
            <Select
              size="large"
              style={{ width: "100%" }}
              placeholder="Filter by subscription"
              value={subscriptionFilter}
              onChange={(value) => {
                setSubscriptionFilter(value);
                setPagination({ ...pagination, current: 1 });
              }}
            >
              <Select.Option value="all">All Customers</Select.Option>
              <Select.Option value="active">Active Subscriptions</Select.Option>
            </Select>
          </Col>
          <Col xs={24} sm={24} md={12} lg={8} xl={6}>
            <Select
              size="large"
              style={{ width: "100%" }}
              placeholder="Sort by paid amount"
              value={paidSort}
              onChange={(value) => {
                setPaidSort(value);
                setPagination({ ...pagination, current: 1 });
              }}
            >
              <Select.Option value="none">Sort: Newest first</Select.Option>
              <Select.Option value="desc">Most paid first</Select.Option>
              <Select.Option value="asc">Least paid first</Select.Option>
            </Select>
          </Col>
          {(searchInput || searchText || subscriptionFilter !== "all" || paidSort !== "none") && (
            <Col xs={24} sm={24} md={12} lg={8} xl={4}>
              <Button onClick={handleClearSearch} size="large" style={{ width: "100%" }}>
                Clear Filters
              </Button>
            </Col>
          )}
        </Row>

          <Table
          columns={columns}
          dataSource={customers}
          loading={loading}
          pagination={{
            current: pagination.current,
            pageSize: pagination.pageSize,
            total: pagination.total,
            showSizeChanger: true,
            showTotal: (total) => `Total ${total} customers${searchText ? " (filtered)" : ""}`,
          }}
          onChange={handleTableChange}
          bordered
          scroll={{ x: 1000 }}
        />
      </Card>

      <SubscriptionModal
        visible={subscriptionModalVisible}
        onCancel={() => {
          setSubscriptionModalVisible(false);
          setSelectedCustomer(null);
          form.resetFields();
        }}
        onSubmit={handleSubscriptionSubmit}
        customer={selectedCustomer}
        subscriptions={subscriptions}
        loading={subscriptionLoading}
        onDelete={handleDeleteSubscription}
        isActive={isSubscriptionActive}
        form={form}
      />
    </div>
  );
};

const mapStateToProps = (store) => ({
  Auth: store.Authentication,
});

export default connect(mapStateToProps)(Customers);

